// Centralized chart colors for consistent styling across the dashboard
// Theme-aware colors: vibrant for dark mode, softer for light mode

// Fixed colors for reading levels (1-10)
const levelColorsDark = {
  1: 'rgba(192, 132, 252, 0.9)',   // Purple - brighter
  2: 'rgba(96, 165, 250, 0.9)',    // Blue - brighter
  3: 'rgba(52, 211, 153, 0.9)',    // Green - brighter
  4: 'rgba(99, 102, 241, 0.9)',    // Indigo - brighter
  5: 'rgba(248, 113, 113, 0.9)',   // Red - brighter
  6: 'rgba(34, 211, 238, 0.9)',    // Cyan - brighter
  7: 'rgba(244, 114, 182, 0.9)',   // Pink - brighter
  8: 'rgba(250, 204, 21, 0.9)',    // Yellow - brighter
  9: 'rgba(251, 146, 60, 0.9)',    // Orange - brighter
  10: 'rgba(163, 230, 53, 0.9)',   // Lime - brighter
};

const levelColorsLight = {
  1: 'rgba(147, 51, 234, 0.8)',    // Purple - deeper
  2: 'rgba(37, 99, 235, 0.8)',     // Blue - deeper
  3: 'rgba(5, 150, 105, 0.8)',     // Green - deeper
  4: 'rgba(67, 56, 202, 0.8)',     // Indigo - deeper
  5: 'rgba(220, 38, 38, 0.8)',     // Red - deeper
  6: 'rgba(6, 182, 212, 0.8)',     // Cyan - deeper
  7: 'rgba(219, 39, 119, 0.8)',    // Pink - deeper
  8: 'rgba(202, 138, 4, 0.8)',     // Yellow - deeper
  9: 'rgba(234, 88, 12, 0.8)',     // Orange - deeper
  10: 'rgba(101, 163, 13, 0.8)',   // Lime - deeper
};

// Fixed colors for languages (by ISO code or name)
const languageColorsDark = {
  'en': 'rgba(96, 165, 250, 0.9)',     // Blue - English
  'ar': 'rgba(192, 132, 252, 0.9)',    // Purple - Arabic
  'fr': 'rgba(52, 211, 153, 0.9)',     // Green - French
  'ja': 'rgba(248, 113, 113, 0.9)',    // Red - Japanese
  'es': 'rgba(99, 102, 241, 0.9)',     // Indigo - Spanish
  'pt': 'rgba(244, 114, 182, 0.9)',    // Pink - Portuguese
  'cy': 'rgba(250, 204, 21, 0.9)',     // Yellow - Welsh
  'hi': 'rgba(251, 146, 60, 0.9)',     // Orange - Hindi
  'bn': 'rgba(34, 211, 238, 0.9)',     // Cyan - Bengali
  'English': 'rgba(96, 165, 250, 0.9)',
  'Arabic': 'rgba(192, 132, 252, 0.9)',
  'French': 'rgba(52, 211, 153, 0.9)',
  'Japanese': 'rgba(248, 113, 113, 0.9)',
  'Spanish': 'rgba(99, 102, 241, 0.9)',
  'Portuguese': 'rgba(244, 114, 182, 0.9)',
  'Welsh': 'rgba(250, 204, 21, 0.9)',
  'Hindi': 'rgba(251, 146, 60, 0.9)',
  'Bengali': 'rgba(34, 211, 238, 0.9)',
};

const languageColorsLight = {
  'en': 'rgba(37, 99, 235, 0.8)',      // Blue - English
  'ar': 'rgba(147, 51, 234, 0.8)',     // Purple - Arabic
  'fr': 'rgba(5, 150, 105, 0.8)',      // Green - French
  'ja': 'rgba(220, 38, 38, 0.8)',      // Red - Japanese
  'es': 'rgba(67, 56, 202, 0.8)',      // Indigo - Spanish
  'pt': 'rgba(219, 39, 119, 0.8)',     // Pink - Portuguese
  'cy': 'rgba(202, 138, 4, 0.8)',      // Yellow - Welsh
  'hi': 'rgba(234, 88, 12, 0.8)',      // Orange - Hindi
  'bn': 'rgba(6, 182, 212, 0.8)',      // Cyan - Bengali
  'English': 'rgba(37, 99, 235, 0.8)',
  'Arabic': 'rgba(147, 51, 234, 0.8)',
  'French': 'rgba(5, 150, 105, 0.8)',
  'Japanese': 'rgba(220, 38, 38, 0.8)',
  'Spanish': 'rgba(67, 56, 202, 0.8)',
  'Portuguese': 'rgba(219, 39, 119, 0.8)',
  'Welsh': 'rgba(202, 138, 4, 0.8)',
  'Hindi': 'rgba(234, 88, 12, 0.8)',
  'Bengali': 'rgba(6, 182, 212, 0.8)',
};

// Fallback colors for unknown values
const fallbackColorsDark = 'rgba(156, 163, 175, 0.9)';
const fallbackColorsLight = 'rgba(107, 114, 128, 0.8)';

/**
 * Get color for a reading level
 * @param {number} level - Reading level (1-10)
 * @param {boolean} isDarkMode - Whether dark mode is active
 * @returns {string} RGBA color string
 */
export const getLevelColor = (level, isDarkMode = true) => {
  const colors = isDarkMode ? levelColorsDark : levelColorsLight;
  const fallback = isDarkMode ? fallbackColorsDark : fallbackColorsLight;
  return colors[level] || fallback;
};

/**
 * Get colors array for an array of levels
 * @param {Array} levels - Array of level numbers
 * @param {boolean} isDarkMode - Whether dark mode is active
 * @returns {Array} Array of RGBA color strings
 */
export const getLevelColors = (levels, isDarkMode = true) => {
  return levels.map(level => getLevelColor(level, isDarkMode));
};

/**
 * Get color for a language
 * @param {string} language - Language code (ISO) or full name
 * @param {boolean} isDarkMode - Whether dark mode is active
 * @returns {string} RGBA color string
 */
export const getLanguageColor = (language, isDarkMode = true) => {
  const colors = isDarkMode ? languageColorsDark : languageColorsLight;
  const fallback = isDarkMode ? fallbackColorsDark : fallbackColorsLight;

  if (!language) return fallback;

  // Try exact match first
  if (colors[language]) {
    return colors[language];
  }

  // Try lowercase
  const lowerLang = language.toLowerCase();
  const keys = Object.keys(colors);
  for (const key of keys) {
    if (key.toLowerCase() === lowerLang) {
      return colors[key];
    }
  }

  // Try to find partial match
  for (const key of keys) {
    if (language.toLowerCase().includes(key.toLowerCase()) ||
        key.toLowerCase().includes(language.toLowerCase())) {
      return colors[key];
    }
  }

  // Generate consistent color based on string hash for unknown languages
  let hash = 0;
  for (let i = 0; i < language.length; i++) {
    hash = language.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  const saturation = isDarkMode ? 75 : 65;
  const lightness = isDarkMode ? 65 : 50;
  const opacity = isDarkMode ? 0.9 : 0.8;
  return `hsla(${hue}, ${saturation}%, ${lightness}%, ${opacity})`;
};

/**
 * Get colors array for an array of languages
 * @param {Array} languages - Array of language codes or names
 * @param {boolean} isDarkMode - Whether dark mode is active
 * @returns {Array} Array of RGBA color strings
 */
export const getLanguageColors = (languages, isDarkMode = true) => {
  return languages.map(lang => getLanguageColor(lang, isDarkMode));
};

export { levelColorsDark, levelColorsLight, languageColorsDark, languageColorsLight };
